import { Dispatch, FC, SetStateAction, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';

interface Props {
  id: string;
  setStudentId: Dispatch<SetStateAction<string | null>>;
  allStudents:
    | {
        readonly nodes:
          | readonly (
              | {
                  readonly firstName: string | null | undefined;
                  readonly id: string;
                  readonly lastName: string | null | undefined;
                  readonly publicUserId: string | null | undefined;
                }
              | null
              | undefined
            )[]
          | null
          | undefined;
      }
    | null
    | undefined;
}

export const IndividualReportStudentList: FC<Props> = ({ id, setStudentId, allStudents }) => {
  const overlayPanelRef = useRef<OverlayPanel>(null);
  const [stdIndex, setStdIndex] = useState<number | null>(null);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const studentList = allStudents?.nodes?.length
    ? [...allStudents.nodes].sort((a, b) => {
        if (a?.lastName && b?.lastName) {
          return a.lastName.localeCompare(b.lastName);
        } else {
          return 0;
        }
      })
    : [];

  const index = studentList.findIndex(st => st?.id === id);
  useEffect(() => {
    setStdIndex(index);
  }, [id]);

  useEffect(() => {
    if (stdIndex !== null && studentList) {
      setStudentId(studentList[stdIndex]?.id || id);
    }
  }, [stdIndex]);

  const toggleOverlay = (e: SyntheticEvent<Element>) => {
    setIsOverlayVisible(prev => !prev);
    overlayPanelRef.current?.toggle(e);
  };

  return (
    <div className="tw-w-full">
      {stdIndex !== null && studentList && studentList[stdIndex]?.id && (
        <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-p-0 tw-mx-0">
          <div className="tw-w-[25%]">
            {stdIndex > 0 && (
              <div
                className="tw-flex tw-items-center tw-cursor-pointer"
                onClick={() => {
                  setStdIndex(prev => (prev !== null ? prev - 1 : prev));
                }}
              >
                <ArrowLeft className="tw-text-blue-500" />
                <div className="tw-ml-2 tw-min-w-0">
                  <h4
                    className="tw-text-blue-500 tw-mx-2"
                    style={{
                      fontFamily: 'Poppins',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Prev:{' '}
                    <MaskedStudentData
                      student={{ publicUserId: studentList[stdIndex - 1]?.publicUserId }}
                      type={PiiTypes.FullName}
                    >
                      {`${studentList[stdIndex - 1]?.firstName} ${studentList[stdIndex - 1]?.lastName}`}
                    </MaskedStudentData>
                  </h4>
                </div>
              </div>
            )}
          </div>
          <div
            onClick={e => toggleOverlay(e)}
            className="tw-flex tw-items-center tw-max-w-[50%]"
            style={{ minWidth: 0 }}
          >
            <h3
              className="tw-text-blue-500 tw-cursor-pointer tw-underline tw-mr-2"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              <MaskedStudentData
                student={{ publicUserId: studentList[stdIndex]?.publicUserId }}
                type={PiiTypes.FullName}
              >
                {`${studentList[stdIndex]?.firstName} ${studentList[stdIndex]?.lastName}`}
              </MaskedStudentData>
            </h3>
            {isOverlayVisible ? (
              <ChevronUp className="tw-text-blue-500" />
            ) : (
              <ChevronDown className="tw-text-blue-500" />
            )}
          </div>
          <OverlayPanel
            onHide={() => setIsOverlayVisible(false)}
            unstyled
            ref={overlayPanelRef}
            className="tw-rounded-lg tw-border-solid tw-border-neutral-300 tw-border-[1px]"
            style={{ display: isOverlayVisible ? 'block' : 'none' }}
          >
            <div className="tw-bg-white tw-p-4 tw-border-1 tw-rounded-xl tw-w-[12rem] tw-max-h-[45vh] tw-overflow-scroll">
              {studentList.map((st, inx) => (
                <div key={st?.id} className="flex align-items-center">
                  <span
                    onClick={() => {
                      setStdIndex(inx);
                      overlayPanelRef.current?.hide();
                      setIsOverlayVisible(false);
                    }}
                    className={`font-semibold block w-full my-2 ${
                      inx !== stdIndex ? 'text-primary cursor-pointer' : ''
                    }`}
                  >
                    <MaskedStudentData student={{ publicUserId: st?.publicUserId }} type={PiiTypes.FullName}>
                      {`${st?.firstName} ${st?.lastName}`}
                    </MaskedStudentData>
                  </span>
                </div>
              ))}
            </div>
          </OverlayPanel>
          <div className="tw-w-[25%] tw-flex tw-justify-end">
            {studentList.length - 1 > stdIndex && (
              <div
                className="tw-flex tw-items-center tw-cursor-pointer tw-min-w-0"
                onClick={() => {
                  setStdIndex(prev => (prev !== null ? prev + 1 : prev));
                }}
              >
                <div className="tw-min-w-0 tw-mr-2">
                  <h4
                    className="tw-text-blue-500"
                    style={{
                      fontFamily: 'Poppins',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <MaskedStudentData
                      student={{ publicUserId: studentList[stdIndex + 1]?.publicUserId }}
                      type={PiiTypes.FullName}
                    >
                      {`${studentList[stdIndex + 1]?.firstName} ${studentList[stdIndex + 1]?.lastName}`}
                    </MaskedStudentData>
                  </h4>
                </div>
                <ArrowRight className="tw-text-blue-500 tw-flex-shrink-0" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
